import { prepareCompactLeagueRows } from "./compactLeagueRows";
import { calculateFixturePerformanceIndex } from "./recentTeamGames";
export const numberOrNull = (v) =>
  v == null || v === "" || typeof v === "boolean" || !Number.isFinite(Number(v))
    ? null
    : Number(v);
export const goalSum = (p) =>
  numberOrNull(p?.home) !== null && numberOrNull(p?.away) !== null
    ? Number(p.home) + Number(p.away)
    : null;
export const goalDifference = (a, b) => ({
  home:
    numberOrNull(a?.home) !== null && numberOrNull(b?.home) !== null
      ? a.home - b.home
      : null,
  away:
    numberOrNull(a?.away) !== null && numberOrNull(b?.away) !== null
      ? a.away - b.away
      : null,
});
// Only completed observations strictly before the selected fixture. Never infer missing values as zero.
export function adaptiveObservations(records = [], before) {
  const cutoff = Date.parse(before);
  if (!Number.isFinite(cutoff)) return [];
  return records
    .filter(
      (r) =>
        r.fixture?.final &&
        Date.parse(r.fixture.kickoff) < cutoff &&
        r.evaluation
    )
    .sort(
      (a, b) => Date.parse(b.fixture.kickoff) - Date.parse(a.fixture.kickoff)
    );
}
export function adaptiveTeamValues(records, teamId) {
  const row = { team_id: teamId, overall: {}, home: {}, away: {} };
  for (const side of ["home", "away"]) {
    const games = records.filter(
      (r) =>
        Number(r.fixture[side + "_team_id"]) === Number(teamId) &&
        goalSum(r.evaluation?.should_goals) !== null
    );
    row[side] = {
      should_games_played: games.length,
      should_goals_scored: games.reduce(
        (n, r) => n + r.evaluation.should_goals[side],
        0
      ),
      should_goals_against: games.reduce(
        (n, r) =>
          n + r.evaluation.should_goals[side === "home" ? "away" : "home"],
        0
      ),
    };
  }
  return prepareCompactLeagueRows([row], "should")[0]?.fixtureSettingValues;
}
export function recentModelRows(records, comparisons, teamId, limit = 8) {
  const byId = new Map(
    (comparisons || []).map((r) => [Number(r.fixture_id), r])
  );
  return records
    .filter((r) =>
      [r.fixture.home_team_id, r.fixture.away_team_id]
        .map(Number)
        .includes(Number(teamId))
    )
    .slice(0, limit)
    .map((r) => {
      const f = r.fixture,
        home = Number(f.home_team_id) === Number(teamId),
        c = byId.get(Number(f.id)),
        actual = r.evaluation.actual_goals || f.goals,
        expected = c?.result_model?.xg,
        should = r.evaluation.should_goals;
      const diff = (p) =>
        goalSum(p) === null ? null : home ? p.home - p.away : p.away - p.home;
      return {
        id: f.id,
        date: f.kickoff,
        home,
        opponent: home ? f.away_name : f.home_name,
        actual,
        expected,
        should,
        statistics: r.pre?.statistic_xg,
        performance: calculateFixturePerformanceIndex({
          realDifference: diff(actual),
          expectedDifference: diff(expected),
          shouldDifference: diff(should),
        }),
      };
    });
}
export function summarizeRecent(rows, key) {
  const complete = rows.filter((r) => goalSum(r[key]) !== null);
  return {
    count: complete.length,
    for: complete.length
      ? complete.reduce((n, r) => n + r[key][r.home ? "home" : "away"], 0)
      : null,
    against: complete.length
      ? complete.reduce((n, r) => n + r[key][r.home ? "away" : "home"], 0)
      : null,
  };
}
// Same home-advantage definition as get-league-reference-stats.finishActual:
// home goals / ((home + away goals) / 2). Home rows count each fixture once.
export function leagueGoalContext(rows = [], view = "actual") {
  const prefix = view === "actual" ? "" : view + "_";
  const usable = rows
    .map((r) => r.home)
    .filter(
      (h) =>
        h &&
        [
          prefix + "games_played",
          prefix + "goals_scored",
          prefix + "goals_against",
        ].every((k) => numberOrNull(h[k]) !== null)
    );
  const sum = (k) => usable.reduce((n, h) => n + Number(h[prefix + k]), 0),
    games = sum("games_played"),
    home = sum("goals_scored"),
    away = sum("goals_against");
  return {
    games,
    average: games > 0 ? (home + away) / games : null,
    homeFactor:
      games > 0 && home + away > 0 ? home / ((home + away) / 2) : null,
  };
}
export function consolidatedRecentRows(
  records,
  comparisons,
  teamId,
  games,
  expectedLedger = [],
  before,
  limit = 8
) {
  if (!Array.isArray(games) || !games.length)
    return recentModelRows(records, comparisons, teamId, limit);
  const byId = new Map(records.map((r) => [Number(r.fixture.id), r])),
    ledger = new Map(expectedLedger.map((r) => [Number(r.fixture_id), r]));
  const merged = games
    .filter((g) => Date.parse(g.startingAt) < Date.parse(before))
    .sort((a, b) => Date.parse(b.startingAt) - Date.parse(a.startingAt))
    .slice(0, limit)
    .map((g) => {
      const stored = byId.get(Number(g.fixtureId)),
        score = String(g.score).match(/^(\d+):(\d+)$/);
      return {
        fixture: {
          id: g.fixtureId,
          home_team_id: g.homeId,
          away_team_id: g.awayId,
          home_name: g.homeName,
          away_name: g.awayName,
          kickoff: g.startingAt,
          final: true,
        },
        pre: stored?.pre,
        evaluation: {
          ...stored?.evaluation,
          actual_goals: score
            ? { home: Number(score[1]), away: Number(score[2]) }
            : null,
        },
      };
    });
  const ids = new Set(
    (comparisons || [])
      .filter((c) => goalSum(c.result_model?.xg) !== null)
      .map((c) => Number(c.fixture_id))
  );
  const extra = merged
    .filter((r) => !ids.has(Number(r.fixture.id)))
    .map((r) => {
      const l = ledger.get(Number(r.fixture.id));
      return {
        fixture_id: r.fixture.id,
        result_model: {
          xg: {
            home: numberOrNull(l?.homegoalexp),
            away: numberOrNull(l?.awaygoalexp),
          },
        },
      };
    });
  return recentModelRows(
    merged,
    [...(comparisons || []), ...extra],
    teamId,
    limit
  );
}
export function adaptiveLeagueContext(records) {
  const rows = records
    .filter((r) => goalSum(r.evaluation?.should_goals) !== null)
    .map((r) => ({
      home: {
        should_games_played: 1,
        should_goals_scored: r.evaluation.should_goals.home,
        should_goals_against: r.evaluation.should_goals.away,
      },
    }));
  return leagueGoalContext(rows, "should");
}
